'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';


interface Testimonial {
  id: number;
  name: string;
  situation: string;
  quote: string;
  image: string;
  alt: string;
  result: string;
}

export default function TestimonialCarousel() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const testimonials: Testimonial[] = [
  {
    id: 1,
    name: "Markéta K.",
    situation: "Hypotéka na první bydlení",
    quote: "Bála jsem se, že si hypotéku nikdy nedovolím. Tým Co-Work mi pomohl pochopit všechny možnosti a našli jsme řešení, které perfektně sedí na můj rozpočet. Dnes mám vlastní byt v Praze a cítím se finančně jistě.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_159d2c8f8-1763294679735.png",
    alt: "Young woman with long brown hair smiling warmly at camera in casual white shirt",
    result: "Vlastní byt za 18 měsíců"
  },
  {
    id: 2,
    name: "Tomáš a Lucie P.",
    situation: "Plánování rodiny",
    quote: "Když jsme čekali první dítě, měli jsme chaos v financích. Co-Work nám pomohl vytvořit plán, který se přizpůsobil našim měnícím se potřebám. Teď máme dvě děti a finanční klid.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d40e2fa7-1765343230401.png",
    alt: "Happy couple in their thirties, man with beard and woman with blonde hair, both smiling",
    result: "Finanční stabilita pro rodinu"
  },
  {
    id: 3,
    name: "Jan M.",
    situation: "Investice a spoření",
    quote: "Roky jsem odkládal investování, protože jsem se bál udělat chybu. Díky trpělivému vysvětlení a jasnému plánu od Co-Work jsem konečně začal. Po dvou letech vidím reálný růst a cítím se sebevědomě.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1739e1531-1763294401544.png",
    alt: "Professional man in his forties with short gray hair wearing dark blue suit",

    result: "Úspěšné investiční portfolio"
  },
  {
    id: 4,
    name: "Eva S.",
    situation: "Změna kariéry",
    quote: "Když jsem měnila práci, potřebovala jsem přehodnotit celé své finance. Co-Work mi pomohl vytvořit plán, který mi dal svobodu udělat kariérní změnu bez finančního stresu. Nejlepší rozhodnutí!",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18d4825d1-1763293841178.png",
    alt: "Professional woman with red hair in burgundy blazer smiling confidently",
    result: "Úspěšný kariérní přechod"
  }];


  const handlePrevious = () => {
    if (!isHydrated) return;
    setCurrentIndex((prev) => prev === 0 ? testimonials.length - 1 : prev - 1);
  };

  const handleNext = () => {
    if (!isHydrated) return;
    setCurrentIndex((prev) => prev === testimonials.length - 1 ? 0 : prev + 1);
  };

  const handleDotClick = (index: number) => {
    if (!isHydrated) return;
    setCurrentIndex(index);
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section className="py-16 lg:py-24 bg-trust">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-5xl font-bold text-trust-foreground mb-6">
              Příběhy našich klientů
            </h2>
            <p className="text-lg text-trust-foreground/80 max-w-3xl mx-auto">
              Skutečné transformace od lidí, kteří našli cestu z finančního chaosu k jasnému plánu.
            </p>
          </div>

          <div className="relative bg-white rounded-2xl shadow-elevated p-8 lg:p-12">
            <div className="absolute top-8 left-8 text-6xl text-primary/10 font-serif">"</div>
            
            <div className="flex flex-col lg:flex-row items-center space-y-8 lg:space-y-0 lg:space-x-8">
              <div className="flex-1 text-center lg:text-left">
                <p className="text-lg lg:text-xl text-text-primary leading-relaxed mb-6 italic">
                  {currentTestimonial.quote}
                </p>
                
                <div className="mb-4">
                  <p className="text-xl font-semibold text-text-primary">
                    {currentTestimonial.name}
                  </p>
                  <p className="text-text-secondary">{currentTestimonial.situation}</p>
                </div>

                <div className="inline-flex items-center space-x-2 bg-success/10 px-4 py-2 rounded-full">
                  <Icon name="CheckCircleIcon" size={20} className="text-success" variant="solid" />
                  <span className="text-sm font-medium text-success">{currentTestimonial.result}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mt-8 pt-8 border-t border-border">
              <button
                onClick={handlePrevious}
                className="w-12 h-12 flex items-center justify-center bg-surface rounded-full hover:bg-primary hover:text-white transition-smooth"
                aria-label="Previous testimonial">

                <Icon name="ChevronLeftIcon" size={24} className="text-current" />
              </button>

              <div className="flex items-center space-x-2">
                {testimonials.map((_, index) =>
                <button
                  key={index}
                  onClick={() => handleDotClick(index)}
                  className={`w-3 h-3 rounded-full transition-smooth ${
                  index === currentIndex ? 'bg-primary w-8' : 'bg-border hover:bg-primary/50'}`
                  }
                  aria-label={`Go to testimonial ${index + 1}`} />

                )}
              </div>

              <button
                onClick={handleNext}
                className="w-12 h-12 flex items-center justify-center bg-surface rounded-full hover:bg-primary hover:text-white transition-smooth"
                aria-label="Next testimonial">

                <Icon name="ChevronRightIcon" size={24} className="text-current" />
              </button>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-trust-foreground/80 mb-4">
              Připojte se k více než 200 spokojeným klientům v Praze
            </p>
            <div className="flex items-center justify-center space-x-1">
              {[1, 2, 3, 4, 5].map((star) =>
              <Icon key={star} name="StarIcon" size={24} className="text-accent" variant="solid" />
              )}
              <span className="ml-2 text-trust-foreground font-semibold">4.9/5 hodnocení</span>
            </div>
          </div>
        </div>
      </div>
    </section>);

}